/**
 * RADERA FIRMAN — bekräftelsevyn bakom den tystaste raden i Inställningar.
 *
 * VILLKOREN PUNKT 10.7 LOVAR DEN HÄR SKÄRMEN ordagrant: "Under Inställningar
 * finns 'Radera firman'. Du bekräftar med firmans organisationsnummer, och
 * raderingen sker direkt och går inte att ångra." Servern har rutten
 * (DELETE_COMPANY, worker/src/modules/company/delete.ts); utan den här vyn gick
 * löftet bara att hålla genom att någon öppnade databasen för hand.
 *
 * EN SKÄRM OCH INTE EN DIALOGRUTA. Den som ska skriva tio siffror för att
 * radera allt ska ha fyra saker framför sig samtidigt, och i den här ordningen
 * (uppgiften, och DeleteCompanyRequest i shared/types.ts som kräver att
 * exporten, oåterkalleligheten och firman står FÖRE fältet):
 *   1. vilken firma — namn OCH org.nr, i rubriken och en gång till precis
 *      ovanför fältet, så att den som skriver numret ser vad han bekräftar
 *   2. att allt försvinner och inte går att ångra
 *   3. att fakturaunderlaget går att ta ut som CSV först, gratis, även i
 *      låsläge — med en knapp dit (beslut 11: exporten ligger aldrig bakom
 *      betalvägg)
 *   4. fältet, och en knapp som säger "för alltid"
 *
 * BARA ÄGAREN. Raden i Inställningar som leder hit renderas inte alls för
 * någon annan. Skärmen har ändå en adress, och en chef som inte äger firman
 * eller en bokförare kan skriva den — han möts då av ett besked i stället för
 * fältet, precis som de andra stamdataskärmarna gör för bokföraren (beslut 07).
 * Servern prövar samma sak en gång till (requires_owner: OWNER_REQUIRED).
 *
 * SERVERN JÄMFÖR NUMRET, INTE KLIENTEN. Förkontrollen nedan sparar bara en
 * resa och ger ett rakare besked; den kan aldrig släppa igenom något servern
 * skulle stoppa. Fel nummer hos servern är VALIDATION_FAILED
 * { field: 'confirm_org_number' } och ingenting raderas.
 *
 * EN FIRMA SOM BETALAR RADERAS INTE. Servern svarar ROW_IN_USE
 * { blocked_by: 'subscription' } i 'active' och 'past_due' (rules.ts,
 * subscriptionStillBills) — annars hade Stripe fortsatt dra pengar för något
 * som inte finns. Kontraktets text för koden är den allmänna ("används på
 * andra ställen"), och shared/errors.ts säger uttryckligen att skärmen skriver
 * en egen för just det här fallet. Läget är dessutom känt redan i ME
 * (subscription_status), så beskedet och vägen till prenumerationen står här
 * INNAN någon skrivit något — en ruta som bara kan svara nej ska inte stå där.
 * Svarar servern ändå ROW_IN_USE (ett gammalt ME) visas samma besked.
 *
 * OP_ID FRYSES VID FÖRSTA TRYCKET (beslut 09), som i NyFirma: planen — op_id
 * och kroppen — låses, och ett omtag skickar byte för byte samma sak. Släpps
 * bara när servern SVARAT och avvisat, eller när användaren själv vill ändra
 * numret. Efter ett nätfel ligger den kvar. MEN REGELN ÄR ENKLARE HÄR: gick det
 * första anropet fram finns firman inte, och medlemskapet inte heller — då är
 * omtaget FORBIDDEN (worker/src/auth/authenticate.ts: company === null), inte
 * ett uppspelat svar. Det är kvittot vi aldrig fick, och det visas inte som ett
 * fel.
 *
 * EFTERÅT: licensen bort ur valvet, sessionen flyttad till en annan firma eller
 * till 'ingen_firma' — allt i session.tsx lamnaFirma, och i SAMMA rendering som
 * adressbytet till startsidan. Ett enda anrop mot den raderade firman efter att
 * licensen glömts hade gett UNAUTHENTICATED i klienten, som då rensar hela
 * valvet och loggar ut kontot.
 */

import { useState } from 'react';
import { DELETE_COMPANY } from '@delat/routes';
import type { DeleteCompanyRequest, OpId } from '@delat/types';
import { atgardFor, granska, klienten, nyUlid } from '../api';
import type { Klartidfel } from '../api';
import { granskaRaderadFirma } from '../api/granskare/konto';
import { useNavigering } from '../app/navigering';
import { useInloggad, useSession } from '../app/session';
import { Falt, Felruta, Knapp, Rubrik, Tomtext, Yta } from '../ui';
import { arOrgnummer, rensaOrgnummer, tillOrgnummer, visaOrgnummer } from './personal-data';
import { glomNyssBetalat } from './prenumeration-data';

interface Plan {
  readonly op_id: OpId;
  /** Kroppen, färdig och frusen. Ett omtag måste skicka BYTE för BYTE samma. */
  readonly begaran: DeleteCompanyRequest;
}

const stycke = { margin: 0 } as const;

/**
 * VET VI INTE OM ANROPET GICK FRAM? Nätet (fetch kastade) och ett svar vi inte
 * kunde tolka (en proxy som svarade HTML, en avbruten kropp). Ett api-fel är
 * däremot ett SVAR: servern har sagt nej, och då hände ingenting.
 */
const utanSvar = (fel: Klartidfel): boolean =>
  fel.slag === 'nat' || fel.slag === 'obegripligt';

/**
 * BESKEDET TILL EN FIRMA SOM BETALAR. Texten är skärmens egen — se filhuvudet
 * och ROW_IN_USE i shared/errors.ts. Samma ruta både när läget syns i ME och
 * när servern svarar ROW_IN_USE.
 */
const Betalarbesked = ({ onTillPrenumerationen }: { readonly onTillPrenumerationen: () => void }) => (
  <Yta rubrik="Prenumerationen måste avslutas först">
    <p style={stycke}>
      Firman betalar fortfarande för Klartid, och så länge prenumerationen löper
      raderar vi ingenting — annars hade kortet dragits för en firma som inte
      finns. Avsluta prenumerationen under Prenumeration. Den gäller perioden
      ut och pausas sedan, och först då går firman att radera härifrån.
    </p>
    <div style={{ marginTop: '0.7rem' }}>
      <Knapp variant="stor-tyst" onClick={onTillPrenumerationen}>
        Till prenumerationen
      </Knapp>
    </div>
  </Yta>
);

export const RaderaFirma = () => {
  const { firmaId, aktiv, arAgare, arLast } = useInloggad();
  const { lamnaFirma } = useSession();
  const { ga, ersatt } = useNavigering();

  const [orgnr, sattOrgnr] = useState('');
  const [plan, sattPlan] = useState<Plan | null>(null);
  const [raderar, sattRaderar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [faltfel, sattFaltfel] = useState<string | null>(null);
  /** Servern har sagt ROW_IN_USE — ett gammalt ME visade inte att firman betalar. */
  const [betalarEnligtServern, sattBetalarEnligtServern] = useState(false);

  const last = plan !== null;
  const orgnrVisat = visaOrgnummer(aktiv.org_number);
  const betalar =
    betalarEnligtServern ||
    aktiv.subscription_status === 'active' ||
    aktiv.subscription_status === 'past_due';

  /**
   * Förkontrollen. Formen som överallt annars (tio siffror, bindestreck är det
   * användaren skriver) och sedan själva jämförelsen mot firmans nummer — som
   * servern gör om (confirmsCompany i rules.ts). Ett fel här kostar ingen resa
   * och säger rakt vad som är fel.
   */
  const byggPlan = (): Plan | null => {
    const siffror = rensaOrgnummer(orgnr);
    if (siffror === '') {
      sattFaltfel('Skriv firmans organisationsnummer.');
      return null;
    }
    if (!arOrgnummer(siffror)) {
      sattFaltfel('Org.nr är tio siffror.');
      return null;
    }
    if (siffror !== String(aktiv.org_number)) {
      sattFaltfel('Numret stämmer inte med firmans. Ingenting har raderats.');
      return null;
    }
    sattFaltfel(null);
    return {
      op_id: granska.opId(nyUlid(), 'op_id'),
      begaran: { confirm_org_number: tillOrgnummer(siffror, 'confirm_org_number') },
    };
  };

  /** Var felet ska synas, och om planen ska släppas. */
  const visaFel = (nytt: Klartidfel): void => {
    if (nytt.slag === 'api' && nytt.code === 'ROW_IN_USE') {
      // Firman betalar. Beskedet och vägen till prenumerationen ersätter
      // fältet — se filhuvudet. Planen släpps: servern har svarat och sagt nej.
      sattBetalarEnligtServern(true);
      sattPlan(null);
      return;
    }
    if (
      nytt.slag === 'api' &&
      nytt.code === 'VALIDATION_FAILED' &&
      nytt.detalj?.['field'] === 'confirm_org_number'
    ) {
      // Servern jämförde och sa nej. Förkontrollen ovan borde ha fångat det,
      // men det är SERVERNS jämförelse som gäller — texten är kontraktets.
      sattFaltfel(nytt.text);
      sattPlan(null);
      return;
    }
    sattFel(nytt);
    // SERVERN HAR SVARAT OCH SAGT NEJ -> ingenting raderades, och fältet får
    // öppnas igen. Vid ett nätfel vet vi inte om anropet gick fram: då ligger
    // planen kvar, och nästa tryck bär samma op_id.
    if (nytt.slag === 'api' && atgardFor(nytt) === 'give_up') sattPlan(null);
  };

  /**
   * FIRMAN ÄR BORTA. Adressbytet och sessionsbytet i SAMMA rendering, så att
   * startsidan ritas för nästa firma — eller världen 'ingen_firma' — och aldrig
   * för den som just raderades. Kassaflaggan (prenumeration-data.ts) är det
   * enda firmaminne som ligger i telefonen utanför valvet; den tas med.
   */
  const klart = (): void => {
    glomNyssBetalat(firmaId);
    ersatt('hem');
    lamnaFirma(firmaId);
  };

  const radera = (): void => {
    const aktuell = plan ?? byggPlan();
    if (aktuell === null) return;
    // Ett OMTAG är ett tryck med en plan som redan ligger — och den ligger bara
    // kvar när vi inte fick något svar. Just då, och bara då, betyder FORBIDDEN
    // att det första anropet gick fram.
    const omtagUtanSvar = plan !== null && fel !== null && utanSvar(fel);
    if (plan === null) sattPlan(aktuell);

    sattRaderar(true);
    sattFel(null);
    void (async () => {
      const svar = await klienten.anrop(
        DELETE_COMPANY,
        { params: { company_id: firmaId }, data: aktuell.begaran, op_id: aktuell.op_id },
        granskaRaderadFirma,
      );
      // Alltid av igen, oavsett utfall: en knapp som fastnar i "Raderar ..."
      // efter ett tappat nät är en app man startar om.
      sattRaderar(false);
      if (!svar.ok) {
        if (omtagUtanSvar && svar.fel.slag === 'api' && svar.fel.code === 'FORBIDDEN') {
          // Medlemskapet finns inte, för firman finns inte: det första anropet
          // gick fram. Kvittot vi aldrig fick — inte ett fel.
          klart();
          return;
        }
        visaFel(svar.fel);
        return;
      }
      klart();
    })();
  };

  const rubrik = (
    <Rubrik titel={aktiv.name} under={`Org.nr ${orgnrVisat} · Radera firman ur Klartid`} />
  );

  /* --- Inte ägaren: besked, inget fält. Servern säger samma sak (OWNER_REQUIRED). --- */
  if (!arAgare) {
    return (
      <>
        {rubrik}
        <Yta rubrik="Bara ägaren raderar firman">
          <p style={stycke}>
            Firman raderas av den som äger den i Klartid, och det är inte du. Vem
            det är står under Personal. Vill ni att firman ska bort är det han som
            trycker.
          </p>
        </Yta>
      </>
    );
  }

  return (
    <>
      {rubrik}

      {/* 2. Följderna. Villkoren punkt 10.7, ordagrant i sak. */}
      <Yta rubrik="Det här försvinner">
        <p style={stycke}>
          Allt som hör till {aktiv.name}: arbetsordrar, tider, mil, utlägg, kunder,
          priser och personal. Raderingen sker direkt och går inte att ångra — vi
          kan inte återskapa raderade uppgifter, och det finns ingen papperskorg.
        </p>
      </Yta>

      {/* 3. Exporten, FÖRE fältet. Beslut 11 och villkoren punkt 9.2: aldrig
          bakom betalvägg, och INVOICE_BASIS är öppen i låsläge. */}
      <Yta rubrik="Exportera först">
        <p style={stycke}>
          {arLast
            ? 'Fakturaunderlaget går att ta ut som CSV nu, gratis — också nu när prenumerationen är pausad. Efter raderingen finns ingenting kvar att hämta.'
            : 'Fakturaunderlaget går att ta ut som CSV nu, gratis, och det fungerar även när prenumerationen är pausad. Efter raderingen finns ingenting kvar att hämta.'}
        </p>
        <div style={{ marginTop: '0.7rem' }}>
          <Knapp variant="stor-tyst" onClick={() => ga('fakturera')}>
            Exportera fakturaunderlaget
          </Knapp>
        </div>
      </Yta>

      {betalar ? (
        <Betalarbesked onTillPrenumerationen={() => ga('prenumeration')} />
      ) : (
        <>
          {/* 1 igen, precis ovanför fältet, och 4. Firman med namn OCH nummer
              intill det fält där numret skrivs — så ser han vad han bekräftar. */}
          <Yta rubrik="Bekräfta">
            <p style={{ ...stycke, marginBottom: '0.6rem' }}>
              Du raderar <b>{aktiv.name}</b>, org.nr <b>{orgnrVisat}</b>.
            </p>
            <Falt
              etikett="Skriv firmans organisationsnummer för att bekräfta"
              varde={orgnr}
              onAndra={sattOrgnr}
              disabled={last}
              inputMode="numeric"
              autoComplete="off"
              placeholder="000000-0000"
              hjalp="Tio siffror. Bindestreck går bra — vi tar bort det."
              {...(faltfel === null ? {} : { fel: faltfel })}
            />
          </Yta>

          {fel !== null && <Felruta fel={fel} />}

          <Knapp disabled={raderar} onClick={radera}>
            {raderar
              ? 'Raderar ...'
              : plan === null
                ? `Radera ${aktiv.name} för alltid`
                : 'Försök igen'}
          </Knapp>

          {last && !raderar && (
            <div style={{ marginTop: '0.5rem' }}>
              <Knapp
                variant="stor-tyst"
                onClick={() => {
                  sattPlan(null);
                  sattFel(null);
                }}
              >
                Ändra numret
              </Knapp>
            </div>
          )}

          {/* BARA VID NÄTFEL — det enda läget där vi inte vet om anropet gick
              fram, och där "Försök igen" gör något annat än "Ändra numret". */}
          {last && !raderar && fel !== null && utanSvar(fel) && (
            <Tomtext>
              Kom inget svar? Tryck Försök igen — anropet bär samma id, så firman
              kan inte raderas två gånger. Gick det första fram är firman redan
              borta, och då tar appen dig vidare.
            </Tomtext>
          )}

          <Tomtext>
            Du loggas ut ur firman i samma stund. Är du med i en annan firma
            hamnar du där; annars kan du lägga upp en ny. Ditt konto och din
            inloggning påverkas inte.
          </Tomtext>
        </>
      )}
    </>
  );
};
